import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Slider } from "@mui/material";
import { styled } from "@mui/system";
import { BsVolumeUpFill } from "react-icons/bs";
import { Link } from "react-router-dom";
import ToDoItem from "../components/ToDoItem";
import NewTaskForm from "../components/NewTaskForm";
import { useTasks } from "../context/TaskContext";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const StyledSlider = styled(Slider)({
  width: "100%",
  color: "#8b5cf6",
  "& .MuiSlider-thumb": {
    height: 22,
    width: 22,
    backgroundColor: "#f8fafc",
    border: "3px solid rgba(99,102,241,0.45)",
  },
  "& .MuiSlider-track": {
    height: 8,
    borderRadius: 999,
  },
  "& .MuiSlider-rail": {
    height: 8,
    opacity: 0.3,
  },
});

const todayId = () => new Date().toISOString().slice(0, 10);

const safeLoad = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (error) {
    console.error(`Failed to load ${key}`, error);
    return fallback;
  }
};

const safeSave = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save ${key}`, error);
  }
};

const defaultTopThree = () => [
  { id: 1, text: "", done: false },
  { id: 2, text: "", done: false },
  { id: 3, text: "", done: false },
];

const SortableTodoItem = ({ todo, index, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: todo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    listStyle: "none",
    opacity: isDragging ? 0.35 : 1,
    cursor: "grab",
    touchAction: "none", // critical for pointer/touch to work on mobile
  };

  return (
    <li ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </li>
  );
};

const loadPlan = (dateId) => {
  if (!dateId) {
    return { topThree: defaultTopThree(), stuckEntries: [] };
  }
  const all = safeLoad("studyPlansV2", {});
  return (
    all[dateId] || {
      topThree: defaultTopThree(),
      stuckEntries: [],
    }
  );
};

const savePlan = (dateId, data) => {
  if (!dateId) return;
  const all = safeLoad("studyPlansV2", {});
  const next = { ...all, [dateId]: data };
  safeSave("studyPlansV2", next);
};

const loadSessions = (dateId) => {
  if (!dateId) return [];
  const all = safeLoad("studySessionsV2", {});
  return all[dateId] || [];
};

const saveSessions = (dateId, sessions) => {
  if (!dateId) return;
  const all = safeLoad("studySessionsV2", {});
  const next = { ...all, [dateId]: sessions };
  safeSave("studySessionsV2", next);
};

const ToDoList = () => {
  // Temporarily hide these UI sections (keep code for later).
  const SHOW_DAILY_TOP_THREE = false;
  const SHOW_STUCK_LOG = false;
  const SHOW_SIDEBAR_EXTRAS = false;

  const {
    todos,
    completedTodos,
    addTask,
    updateTask,
    toggleTaskCompletion,
    reorderTodos,
    deleteTask,
    startTask,
    pauseTask,
    startNextTask,
    lists,
    selectedDate,
    ensureListForDate,
    listSummaries,
    loading,
    problemsByTask,
    latestProblemByTask,
    startProblem,
    finishProblem,
    closeProblemReview,
    recordHint,
    requestHint,
    updateProblem,
    reportLatexMismatch,
    analyzeAndCreateProblemFromText,
    analyzeAndCreateProblemFromImage,
    ocrLatexFromImage,
    checkAnswerForProblem,
  } = useTasks();

  const [volume, setVolume] = useState(24);
  const [showCompleted, setShowCompleted] = useState(true);
  const [topThree, setTopThree] = useState(defaultTopThree);
  const [sessionLabel, setSessionLabel] = useState("");
  const [sessions, setSessions] = useState([]);
  const [stuckForm, setStuckForm] = useState({
    description: "",
    category: "Trig",
    tried: "",
    status: "Unresolved",
  });
  const [stuckEntries, setStuckEntries] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Drag won't start until the user has moved 8px — prevents
      // accidental drags when clicking the timer/play buttons on a task.
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [prepConfig, setPrepConfig] = useState(() =>
    safeLoad("studyPrep", { start: "", weeks: 5 })
  );
  const handleVolumeChange = (_, newValue) => {
    setVolume(newValue);
  };

  const handleNewTask = useCallback(
    async (
      task,
      primaryDuration,
      secondaryDuration,
      numCycles,
      tilDone,
      isProblemSolving,
      hintDelaysSec
    ) => {
      await addTask({
        task,
        primaryDuration,
        secondaryDuration,
        numCycles,
        tilDone,
        isProblemSolving,
        hintDelaysSec,
      });
    },
    [addTask]
  );

  const handleUpdate = useCallback(
    async (updatedTask) => {
      const { id, ...updates } = updatedTask;
      if (!id) return;
      await updateTask(id, updates);
    },
    [updateTask]
  );

  const handleToggle = useCallback(
    async (id, completed) => {
      await toggleTaskCompletion(id, completed);
    },
    [toggleTaskCompletion]
  );

  const handleDelete = useCallback(
    async (id) => {
      await deleteTask(id);
    },
    [deleteTask]
  );

  const handleStartTask = useCallback(
    async (taskId) => {
      await startTask(taskId);
    },
    [startTask]
  );

  const handlePauseTask = useCallback(
    async (taskId) => {
      await pauseTask(taskId);
    },
    [pauseTask]
  );

  const handleStartProblem = useCallback(
    async (taskId, payload) => {
      await startProblem(taskId, payload);
    },
    [startProblem]
  );

  const handleFinishProblem = useCallback(
    async (problemId, payload) => {
      await finishProblem(problemId, payload);
    },
    [finishProblem]
  );

  const handleCloseProblemReview = useCallback(
    async (problemId, taskId) => {
      await closeProblemReview(problemId, taskId);
    },
    [closeProblemReview]
  );

  const handleRecordHint = useCallback(
    async (problemId, level, resourceId) => {
      await recordHint(problemId, level, resourceId);
    },
    [recordHint]
  );

  const handleRequestHint = useCallback(
    async (problemId, level, payload) => {
      await requestHint(problemId, level, payload);
    },
    [requestHint]
  );


  const handleAnalyzeAndCreateProblemFromText = useCallback(
    async (taskId, text) => {
      await analyzeAndCreateProblemFromText(taskId, text);
    },
    [analyzeAndCreateProblemFromText]
  );

  const handleAnalyzeAndCreateProblemFromImage = useCallback(
    async (taskId, file) => {
      await analyzeAndCreateProblemFromImage(taskId, file);
    },
    [analyzeAndCreateProblemFromImage]
  );


  const logStudySession = useCallback(
    (label, minutes, completed = true) => {
      const entry = {
        id: Date.now(),
        label: label?.trim() || "Pomodoro session",
        minutes: Math.max(1, Math.round(minutes || 25)),
        completed,
        timestamp: new Date().toISOString(),
      };
      setSessions((prev) => {
        const next = [entry, ...prev].slice(0, 50);
        safeSave("studySessions", next);
        return next;
      });
    },
    []
  );

  const logCompletionIfPossible = useCallback(
    (completedId) => {
      const task =
        todos.find((t) => t.id === completedId) ||
        completedTodos.find((t) => t.id === completedId);
      if (!task) return;
      const cycles = task.tilDone ? 1 : Math.max(1, task.numCycles || 1);
      const minutes = Math.max(
        1,
        Math.round(((task.primaryDuration || 0) * cycles) / 60)
      );
      logStudySession(sessionLabel || task.task, minutes, true);
    },
    [completedTodos, logStudySession, sessionLabel, todos]
  );

  useEffect(() => {
    if (!selectedDate) return;
    const plan = loadPlan(selectedDate);
    setTopThree(plan.topThree || defaultTopThree());
    setStuckEntries(plan.stuckEntries || []);
    setSessions(loadSessions(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate) return;
    savePlan(selectedDate, { topThree, stuckEntries });
  }, [selectedDate, topThree, stuckEntries]);

  useEffect(() => {
    if (!selectedDate) return;
    saveSessions(selectedDate, sessions);
  }, [selectedDate, sessions]);

  const updateTopItem = (id, updates) => {
    setTopThree((prev) => {
      return prev.map((item) => (item.id === id ? { ...item, ...updates } : item));
    });
  };

  const resetTopThree = () => {
    setTopThree(defaultTopThree());
  };

  const addStuckEntry = (event) => {
    event.preventDefault();
    if (!stuckForm.description.trim()) return;
    const entry = {
      id: Date.now(),
      ...stuckForm,
      timestamp: new Date().toISOString(),
    };
    setStuckEntries((prev) => {
      return [entry, ...prev];
    });
    setStuckForm({
      description: "",
      category: stuckForm.category,
      tried: "",
      status: "Unresolved",
    });
  };

  const toggleStuckStatus = (id) => {
    setStuckEntries((prev) => {
      const next = prev.map((item) =>
        item.id === id
          ? {
              ...item,
              status: item.status === "Resolved" ? "Unresolved" : "Resolved",
            }
          : item
      );
      return next;
    });
  };

  const savePrepConfig = (partial) => {
    setPrepConfig((prev) => {
      const next = { ...prev, ...partial };
      safeSave("studyPrep", next);
      return next;
    });
  };

const computePrepWeek = () => {
  if (!prepConfig.start || !prepConfig.weeks) return 1;
  const start = new Date(prepConfig.start);
  if (Number.isNaN(start.getTime())) return 1;
  const diffMs = Date.now() - start.getTime();
    const weeksElapsed = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 7));
    const current = Math.min(
      Math.max(1, weeksElapsed + 1),
      Math.max(1, Number(prepConfig.weeks) || 1)
    );
    return current;
  };

  const currentPrepWeek = computePrepWeek();

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      setActiveId(null);
      if (!over || active.id === over.id) return;
      const fromIndex = todos.findIndex((t) => t.id === active.id);
      const toIndex = todos.findIndex((t) => t.id === over.id);
      if (fromIndex === -1 || toIndex === -1) return;
      const next = arrayMove(todos, fromIndex, toIndex);
      reorderTodos(next);
    },
    [reorderTodos, todos]
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const activeTodo = useMemo(
    () => (activeId ? todos.find((t) => t.id === activeId) : null),
    [activeId, todos]
  );

  const handleDateSelect = useCallback(
    async (value) => {
      if (!value) return;
      await ensureListForDate(value);
    },
    [ensureListForDate]
  );

  const handleCompleteTask = useCallback(
    async (completedId) => {
      logCompletionIfPossible(completedId);
      await startNextTask(completedId);
    },
    [logCompletionIfPossible, startNextTask]
  );

  const problemLookup = useMemo(
    () => latestProblemByTask || {},
    [latestProblemByTask]
  );

  const problemHistoryLookup = useMemo(
    () => problemsByTask || {},
    [problemsByTask]
  );

  const selectedListExists =
    !!selectedDate && lists.some((list) => list.id === selectedDate);
  const selectedSummary =
    listSummaries[selectedDate] || { todos: todos.length, completed: completedTodos.length };
  const friendlyDate = useMemo(() => {
    if (!selectedDate) return "Unscheduled";
    const normalized = selectedDate;
    const date = new Date(`${normalized}T00:00:00`);
    if (Number.isNaN(date.getTime())) return normalized;
    return date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedDate]);

  return (
    <div className="app-layout">
      <header className="page-header">
        <div className="page-header__content">
          <h1>Dashboard</h1>
          <p>
            Line up your focus blocks, track sessions, and log stuck points without losing your Pomodoro flow.
          </p>
          <div className="stat-row" style={{ marginTop: "10px" }}>
            <span className="stat-chip">{todos.length} active</span>
            <span className="stat-chip">{completedTodos.length} done</span>
            <span className="stat-chip">{lists.length || "No"} lists</span>
          </div>
        </div>
        <div className="page-header__aside">
          <span className="badge">{todos.length} active</span>
          <span className="badge">{completedTodos.length} completed</span>
          <div className="page-header__date" title={`Planning for ${friendlyDate}`}>
            <span className="page-header__date-label">Date</span>
            <input
              type="date"
              value={selectedDate || todayId()}
              onChange={(event) => handleDateSelect(event.target.value)}
              aria-label="Planning date"
            />
          </div>
        </div>
      </header>

      <div className="workspace-grid">
        <div className="workspace-main">
          <section className="page-section">
            <div className="page-section__headline">
              <h2>New task</h2>
              <span className="pill">Pomodoro ready</span>
            </div>
            <NewTaskForm onSubmit={handleNewTask} disabled={!selectedListExists} />
          </section>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
          >
            <section className="page-section">
              <div className="task-list-header">
                <div>
                  <h2 style={{ marginBottom: "4px" }}>
                    {loading ? "Loading tasks…" : "Not yet done"}
                  </h2>
                  <p className="section-caption" style={{ margin: 0 }}>
                    Drag to reprioritise your queue.
                  </p>
                </div>
                <span className="pill-ghost">
                  {selectedSummary.todos} open · {selectedSummary.completed} done
                </span>
              </div>

              <SortableContext
                items={todos.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="task-list">
                  {todos.map((todo, index) => (
                    <SortableTodoItem
                      key={todo.id}
                      todo={todo}
                      index={index}
                    >
                      <ToDoItem
                        handleUpdate={handleUpdate}
                        index={index}
                        id={todo.id}
                        task={todo.task}
                        complete={todo.complete}
                        primaryDuration={todo.primaryDuration}
                        secondaryDuration={todo.secondaryDuration}
                        numCycles={todo.numCycles}
                        onToggle={() => handleToggle(todo.id)}
                        onDelete={() => handleDelete(todo.id)}
                        tilDone={todo.tilDone}
                        draggableId={todo.id.toString()}
                        volume={volume}
                        order={todo.order}
                        isRunning={todo.isRunning}
                        onStart={() => handleStartTask(todo.id)}
                        onPause={() => handlePauseTask(todo.id)}
                        onComplete={handleCompleteTask}
                        isProblemSolving={todo.isProblemSolving}
                        hintDelaysSec={todo.hintDelaysSec}
                        problem={problemLookup[todo.id]}
                        problems={problemHistoryLookup[todo.id]}
                        onStartProblem={handleStartProblem}
                        onFinishProblem={handleFinishProblem}
                        onCloseReview={handleCloseProblemReview}
                        onRecordHint={handleRecordHint}
                        onRequestHint={handleRequestHint}
                        onAnalyzeAndCreateProblemFromText={handleAnalyzeAndCreateProblemFromText}
                        onAnalyzeAndCreateProblemFromImage={handleAnalyzeAndCreateProblemFromImage}
                        onOcrLatexFromImage={ocrLatexFromImage}
                        onCheckAnswerForProblem={checkAnswerForProblem}
                        onUpdateProblem={updateProblem}
                        onReportLatexMismatch={reportLatexMismatch}
                      />
                    </SortableTodoItem>
                  ))}
                  {!todos.length && (
                    <li className="empty-state">
                      <strong>No tasks queued</strong>
                      Use the form above or move items out of “Donzo” to build
                      your plan for the day.
                    </li>
                  )}
                </ul>
              </SortableContext>
            </section>

            <section className="page-section page-section--subtle">
              <div className="page-section__headline">
                <div>
                  <h2 style={{ marginBottom: "4px" }}>Donzo</h2>
                  <p className="section-caption" style={{ margin: 0 }}>
                    Completed tasks stay here for the day.
                  </p>
                </div>
                <div className="cluster" style={{ gap: "10px" }}>
                  <span className="badge">{completedTodos.length} completed</span>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setShowCompleted((prev) => !prev)}
                  >
                    {showCompleted ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              {showCompleted ? (
                <ul className="task-list">
                  {completedTodos.map((todo, index) => (
                    <li key={todo.id}>
                      <ToDoItem
                        handleUpdate={handleUpdate}
                        index={index}
                        id={todo.id}
                        task={todo.task}
                        complete={todo.complete}
                        primaryDuration={todo.primaryDuration}
                        secondaryDuration={todo.secondaryDuration}
                        numCycles={todo.numCycles}
                        onToggle={() => handleToggle(todo.id, false)}
                        onDelete={() => handleDelete(todo.id)}
                        tilDone={todo.tilDone}
                        draggableId={todo.id.toString()}
                        volume={volume}
                        isRunning={todo.isRunning}
                        onComplete={handleCompleteTask}
                        isProblemSolving={todo.isProblemSolving}
                        hintDelaysSec={todo.hintDelaysSec}
                        problem={problemLookup[todo.id]}
                        problems={problemHistoryLookup[todo.id]}
                        onStartProblem={handleStartProblem}
                        onFinishProblem={handleFinishProblem}
                        onCloseReview={handleCloseProblemReview}
                        onRecordHint={handleRecordHint}
                        onRequestHint={handleRequestHint}
                        onAnalyzeAndCreateProblemFromText={handleAnalyzeAndCreateProblemFromText}
                        onAnalyzeAndCreateProblemFromImage={handleAnalyzeAndCreateProblemFromImage}
                        onOcrLatexFromImage={ocrLatexFromImage}
                        onCheckAnswerForProblem={checkAnswerForProblem}
                        onUpdateProblem={updateProblem}
                        onReportLatexMismatch={reportLatexMismatch}
                      />
                    </li>
                  ))}
                  {!completedTodos.length && (
                    <li className="empty-state">
                      <strong>No completed tasks yet</strong>
                      When you mark an item done it will land here for
                      safekeeping.
                    </li>
                  )}
                </ul>
              ) : (
                <div className="empty-state" style={{ marginTop: "8px" }}>
                  <strong>Hidden</strong>
                  Completed tasks are tucked away. Show them when you need them.
                </div>
              )}
            </section>
            <DragOverlay
              dropAnimation={{
                duration: 220,
                easing: "cubic-bezier(0.18, 0.67, 0.43, 1.1)",
              }}
            >
              {activeTodo ? (
                <div
                  className="task-list__drag-preview"
                  style={{
                    boxShadow:
                      "0 18px 40px -12px rgba(15,23,42,0.55), 0 8px 18px -8px rgba(99,102,241,0.4)",
                    borderRadius: 16,
                    cursor: "grabbing",
                    background: "rgba(15,23,42,0.92)",
                    transform: "rotate(1.5deg)",
                  }}
                >
                  <ToDoItem
                    handleUpdate={handleUpdate}
                    index={todos.findIndex((t) => t.id === activeTodo.id)}
                    id={activeTodo.id}
                    task={activeTodo.task}
                    complete={activeTodo.complete}
                    primaryDuration={activeTodo.primaryDuration}
                    secondaryDuration={activeTodo.secondaryDuration}
                    numCycles={activeTodo.numCycles}
                    onToggle={() => {}}
                    onDelete={() => {}}
                    tilDone={activeTodo.tilDone}
                    draggableId={activeTodo.id.toString()}
                    volume={volume}
                    order={activeTodo.order}
                    isRunning={activeTodo.isRunning}
                    onStart={() => {}}
                    onPause={() => {}}
                    onComplete={() => {}}
                    isProblemSolving={activeTodo.isProblemSolving}
                    hintDelaysSec={activeTodo.hintDelaysSec}
                    problem={problemLookup[activeTodo.id]}
                    problems={problemHistoryLookup[activeTodo.id]}
                    onStartProblem={() => {}}
                    onFinishProblem={() => {}}
                    onCloseReview={() => {}}
                    onRecordHint={() => {}}
                    onRequestHint={() => {}}
                    onAnalyzeAndCreateProblemFromText={() => {}}
                    onAnalyzeAndCreateProblemFromImage={() => {}}
                    onOcrLatexFromImage={() => {}}
                    onCheckAnswerForProblem={() => {}}
                    onUpdateProblem={() => {}}
                    onReportLatexMismatch={() => {}}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {SHOW_DAILY_TOP_THREE && (
          <section className="page-section page-section--subtle page-section--condensed">
            <div className="page-section__headline">
              <div>
                <h2>Daily Top 3</h2>
                <p className="section-caption" style={{ margin: 0 }}>
                  Set today’s three most important study tasks. They reset each day.
                </p>
              </div>
              <button type="button" className="btn btn--ghost" onClick={resetTopThree}>
                Reset for new day
              </button>
            </div>
            <div className="stack stack--dense">
              {topThree.map((item) => (
                <div key={item.id} className="task-form__row" style={{ alignItems: "center" }}>
                  <label className="checkbox-field" style={{ flex: "0 0 24px" }}>
                    <input
                      type="checkbox"
                      checked={item.done}
                      onChange={() => updateTopItem(item.id, { done: !item.done })}
                    />
                    <span></span>
                  </label>
                  <input
                    type="text"
                    placeholder={`Top task ${item.id}`}
                    value={item.text}
                    onChange={(e) => updateTopItem(item.id, { text: e.target.value })}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
              <p className="mini-label" style={{ marginTop: "6px" }}>
                Saved for {selectedDate || todayId()}
              </p>
            </div>
          </section>

          )}

          {SHOW_STUCK_LOG && (
          <section className="page-section">
            <div className="page-section__headline">
              <div>
                <h2>Stuck log</h2>
                <p className="section-caption" style={{ margin: 0 }}>
                  Capture problems, what you tried, and mark when resolved.
                </p>
              </div>
            </div>
            <form className="task-form" onSubmit={addStuckEntry}>
              <div className="task-form__row">
                <div className="task-form__field">
                  <label>Description</label>
                  <input
                    type="text"
                    value={stuckForm.description}
                    onChange={(e) =>
                      setStuckForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Briefly describe the problem"
                  />
                </div>
                <div className="task-form__field" style={{ maxWidth: "200px" }}>
                  <label>Category</label>
                  <select
                    value={stuckForm.category}
                    onChange={(e) =>
                      setStuckForm((prev) => ({ ...prev, category: e.target.value }))
                    }
                  >
                    <option>Trig</option>
                    <option>Substitution</option>
                    <option>Series</option>
                    <option>Sequences</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div className="task-form__row">
                <div className="task-form__field">
                  <label>What I tried</label>
                  <textarea
                    rows={3}
                    value={stuckForm.tried}
                    onChange={(e) =>
                      setStuckForm((prev) => ({ ...prev, tried: e.target.value }))
                    }
                    placeholder="Steps you attempted before asking for help"
                  />
                </div>
                <div className="task-form__field" style={{ maxWidth: "200px" }}>
                  <label>Status</label>
                  <select
                    value={stuckForm.status}
                    onChange={(e) =>
                      setStuckForm((prev) => ({ ...prev, status: e.target.value }))
                    }
                  >
                    <option>Unresolved</option>
                    <option>Resolved</option>
                  </select>
                </div>
              </div>
              <button type="submit" className="btn btn--primary" style={{ alignSelf: "flex-start" }}>
                Add entry
              </button>
            </form>

            <div className="mini-grid" style={{ marginTop: "16px" }}>
              {stuckEntries.length === 0 && (
                <div className="empty-state" style={{ gridColumn: "1 / -1" }}>
                  <strong>No stuck items yet</strong>
                  Log tricky problems so you can revisit and resolve them.
                </div>
              )}
              {stuckEntries.map((entry) => (
                <div key={entry.id} className="card-quiet">
                  <div className="page-section__headline" style={{ marginBottom: "6px" }}>
                    <div className="stack stack--dense" style={{ gap: "6px" }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>
                        {entry.description.length > 80
                          ? `${entry.description.slice(0, 80)}…`
                          : entry.description}
                      </p>
                      <p className="mini-label">
                        {entry.category} · {new Date(entry.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => toggleStuckStatus(entry.id)}
                    >
                      {entry.status === "Resolved" ? "Mark unresolved" : "Mark resolved"}
                    </button>
                  </div>
                  {entry.tried && (
                    <p className="mini-label" style={{ lineHeight: 1.6 }}>
                      Tried: {entry.tried}
                    </p>
                  )}
                  <span
                    className="pill"
                    style={{
                      borderColor:
                        entry.status === "Resolved"
                          ? "rgba(34,197,94,0.45)"
                          : "rgba(249,115,22,0.45)",
                      color:
                        entry.status === "Resolved"
                          ? "rgba(34,197,94,0.9)"
                          : "rgba(249,115,22,0.9)",
                    }}
                  >
                    {entry.status}
                  </span>
                </div>
              ))}
            </div>
          </section>
          )}
        </div>

        <aside className="workspace-aside">
          <div className="card-quiet">
            <div className="page-section__headline" style={{ marginBottom: "10px" }}>
              <h3 style={{ margin: 0 }}>Timer settings</h3>
              <span className="pill" style={{ fontSize: "0.8rem" }}>Bell</span>
            </div>
            <p className="mini-label">Volume</p>
            <div className="cluster" style={{ alignItems: "center", gap: "10px" }}>
              <button type="button" className="btn-icon btn--ghost">
                <BsVolumeUpFill size={18} />
              </button>
              <div style={{ width: "100%" }}>
                <StyledSlider
                  value={volume}
                  onChange={handleVolumeChange}
                  aria-labelledby="volume-slider"
                />
              </div>
            </div>
            <p className="mini-label" style={{ marginTop: "10px" }}>
              Applies to bell and applause when running tasks.
            </p>
          </div>

          {SHOW_SIDEBAR_EXTRAS && (
          <>
          <div className="card-quiet">
            <h3>List summary</h3>
            <div className="mini-grid">
              <div>
                <p className="mini-label">Open</p>
                <p style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700 }}>
                  {selectedSummary.todos}
                </p>
              </div>
              <div>
                <p className="mini-label">Done</p>
                <p style={{ margin: 0, fontSize: "1.4rem", fontWeight: 700 }}>
                  {selectedSummary.completed}
                </p>
              </div>
            </div>
            <p className="mini-label" style={{ marginTop: "10px" }}>
              {friendlyDate}
            </p>
          </div>

          <div className="card-quiet">
            <h3>Session log</h3>
            <div className="stack stack--dense">
              <div className="task-form__field">
                <label>Session label</label>
                <input
                  type="text"
                  placeholder="e.g. Trig identities drill"
                  value={sessionLabel}
                  onChange={(e) => setSessionLabel(e.target.value)}
                />
                <p className="mini-label" style={{ marginTop: "6px" }}>
                  Used when a task completes to label the logged session.
                </p>
              </div>
              <div className="stack stack--dense">
                <p className="mini-label" style={{ margin: 0 }}>
                  Recent sessions
                </p>
                {sessions.length === 0 && (
                  <p className="mini-label">No sessions logged yet.</p>
                )}
                {sessions.slice(0, 10).map((session) => (
                  <div
                    key={session.id}
                    className="card-quiet"
                    style={{ padding: "10px 12px", borderRadius: "12px" }}
                  >
                    <p style={{ margin: "0 0 4px" }}>{session.label}</p>
                    <p className="mini-label" style={{ margin: 0 }}>
                      {session.minutes} min ·{" "}
                      {new Date(session.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card-quiet">
            <h3>Prep progress</h3>
            <div className="stack stack--dense">
              <div className="task-form__field">
                <label>Prep start date</label>
                <input
                  type="date"
                  value={prepConfig.start}
                  onChange={(e) => savePrepConfig({ start: e.target.value })}
                />
              </div>
              <div className="task-form__field" style={{ maxWidth: "140px" }}>
                <label>Total weeks</label>
                <input
                  type="number"
                  min={1}
                  value={prepConfig.weeks}
                  onChange={(e) => {
                    const val = Math.max(1, parseInt(e.target.value || "1", 10));
                    savePrepConfig({ weeks: val });
                  }}
                />
              </div>
              <div className="mini-grid">
                {Array.from({ length: Math.max(1, Number(prepConfig.weeks) || 1) }).map(
                  (_, idx) => {
                    const weekNum = idx + 1;
                    const isCurrent = weekNum === currentPrepWeek;
                    const isComplete = weekNum < currentPrepWeek;
                    return (
                      <div
                        key={weekNum}
                        className="pill"
                        style={{
                          justifyContent: "center",
                          borderColor: isCurrent
                            ? "rgba(99,102,241,0.6)"
                            : isComplete
                            ? "rgba(34,197,94,0.4)"
                            : "rgba(148,163,184,0.28)",
                          color: isCurrent
                            ? "rgba(196,201,255,0.95)"
                            : isComplete
                            ? "rgba(34,197,94,0.9)"
                            : "rgba(226,232,240,0.75)",
                          background: isCurrent
                            ? "rgba(99,102,241,0.16)"
                            : isComplete
                            ? "rgba(34,197,94,0.12)"
                            : "rgba(15,23,42,0.55)",
                        }}
                      >
                        Week {weekNum}
                      </div>
                    );
                  }
                )}
              </div>
              <p className="mini-label" style={{ margin: 0 }}>
                Current: Week {currentPrepWeek} of {prepConfig.weeks || 1}
              </p>
            </div>
          </div>

          <div className="card-quiet">
            <h3>Shortcuts</h3>
            <div className="stack stack--dense">
              <Link className="pill-link" to="/Prio">
                Prioritize tasks
              </Link>
              <Link className="pill-link" to="/Calendar">
                View calendar
              </Link>
            </div>
          </div>
          </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ToDoList;
